import React, { useState, useEffect } from 'react';
import {
  HiX,
  HiOutlineUserCircle,
  HiOutlineCalendar,
  HiOutlineTag,
  HiOutlineExclamation,
  HiOutlinePlus,
  HiOutlineOfficeBuilding
} from 'react-icons/hi';
import { db } from '../../../../firebase/config';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { useSelector, useDispatch } from 'react-redux';
import { createNotification } from '../../../../store/slices/notificationSlice';
import { sendTaskAssignmentEmails } from '../../../../emailjs/emailService';
import './AssignTaskModal.scss';

const AssignTaskModal = ({ show, onClose, employees, onTaskCreated }) => {
  const dispatch = useDispatch();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [selectedAssignees, setSelectedAssignees] = useState([]);
  const [priority, setPriority] = useState('Medium');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [roleBasedAssignment, setRoleBasedAssignment] = useState(false);
  const [roleAssignments, setRoleAssignments] = useState([
    { email: '', role: 'Task Coordinator' }
  ]);
  const [searchTerm, setSearchTerm] = useState('');
  const [department, setDepartment] = useState('All');
  const [sendEmails, setSendEmails] = useState(true);

  const currentUser = useSelector(state => state.auth.user);
  
  // Department options from employee data
  const departments = employees && Array.isArray(employees) ? 
    ['All', ...new Set(employees
      .filter(emp => emp.department)
      .map(emp => emp.department))] : ['All'];

  // Generate tomorrow's date as default due date
  useEffect(() => {
    if (!dueDate) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(17, 0, 0, 0); // Set to 5:00 PM
      setDueDate(tomorrow.toISOString().slice(0, 16)); // Format as YYYY-MM-DDThh:mm
    }
  }, [dueDate]);

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setDueDate('');
    setSelectedAssignees([]);
    setPriority('Medium');
    setError('');
    setSuccessMessage('');
    setRoleBasedAssignment(false);
    setRoleAssignments([{ email: '', role: 'Task Coordinator' }]);
    setSearchTerm('');
    setDepartment('All');
    setSendEmails(true);
  };

  const handleClose = (e) => {
    if (e) e.preventDefault();
    resetForm();
    onClose();
  };

  // Find an employee by email
  const findEmployeeByEmail = (email) => {
    if (!employees || !Array.isArray(employees)) return null;
    return employees.find(emp => emp.email === email);
  };

  // Get email from employee data - in this database structure, id is the email
  const getEmailFromEmployee = (employeeId) => {
    if (!employees || !Array.isArray(employees)) return null;
    const employee = employees.find(emp => emp.id === employeeId);
    return employee ? employee.email : null;
  };

  // Format the employee name for display
  const getEmployeeName = (email) => {
    const employee = findEmployeeByEmail(email);
    return employee ? employee.name || email : email;
  };

  // Get employee firstName for email personalization
  const getEmployeeFirstName = (email) => {
    const employee = findEmployeeByEmail(email);
    if (!employee || !employee.name) return '';
    return employee.name.split(' ')[0];
  };

  // Get full employee data by email
  const getEmployeeDataByEmail = (email) => {
    const employee = findEmployeeByEmail(email);
    if (!employee) return { email };
    return employee;
  };

  // Handle adding role assignments
  const addRoleAssignment = () => {
    setRoleAssignments([...roleAssignments, { email: '', role: 'Task Coordinator' }]);
  };

  // Handle removing role assignments
  const removeRoleAssignment = (index) => {
    const updatedAssignments = [...roleAssignments];
    updatedAssignments.splice(index, 1);
    setRoleAssignments(updatedAssignments);
  };

  // Handle updating role assignments
  const updateRoleAssignment = (index, field, value) => {
    const updatedAssignments = [...roleAssignments];
    updatedAssignments[index][field] = value;
    setRoleAssignments(updatedAssignments);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSubmitting(true);
      setError('');
      setSuccessMessage('');
      
      // Validation
      if (!title.trim()) {
        setError('Task title is required');
        setSubmitting(false);
        return;
      }
      
      if (!dueDate) {
        setError('Due date is required');
        setSubmitting(false);
        return;
      }
      
      // Validate role assignments if that option is selected
      if (roleBasedAssignment) {
        const isValid = roleAssignments.every(item => item.email && item.role);
        if (!isValid) {
          setError('All role assignments must have both email and role specified');
          setSubmitting(false);
          return;
        }
        
        if (roleAssignments.length === 0) {
          setError('At least one role assignment is required');
          setSubmitting(false);
          return;
        }
      } else if (selectedAssignees.length === 0) {
        setError('At least one assignee is required');
        setSubmitting(false);
        return;
      }
      
      // Format assignees from IDs to email addresses
      let assigneeEmails = [];
      if (!roleBasedAssignment) {
        assigneeEmails = selectedAssignees.map(id => {
          return getEmailFromEmployee(id);
        }).filter(Boolean);
      } else {
        assigneeEmails = roleAssignments.map(item => item.email);
      }
      
      // Create task in Firestore
      const taskData = {
        title,
        description,
        details: description,
        endDate: new Date(dueDate),
        assignees: assigneeEmails,
        priority,
        status: 'New task',
        createdAt: serverTimestamp(),
        lastUpdated: serverTimestamp(),
        lastUpdatedBy: currentUser.email || currentUser.uid,
        lastUpdatedIncident: 'task created',
        liveStatus: 'active',
        roleBasedAssignment,
        roleBasedAssignees: roleBasedAssignment ? roleAssignments : []
      };
      
      const docRef = await addDoc(collection(db, 'assignedTasks'), taskData);
      
      // Format due date for display
      const formattedDueDate = new Date(dueDate).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
      
      const assignerName = currentUser.displayName || currentUser.email;
      const assignerData = {
        name: assignerName,
        email: currentUser.email
      };
      
      // Create notifications for each assignee
      const notificationPromises = [];
      
      for (const email of assigneeEmails) {
        if (!email) continue;
        
        // Create notification content
        const notificationTitle = `New Task Assigned: ${title}`;
        const notificationMessage = `${assignerName} has assigned you a new task with priority ${priority}, due on ${formattedDueDate}.`;
        
        // Create notification with email data if email sending is enabled
        const notificationPromise = dispatch(createNotification({
          userEmail: email,
          title: notificationTitle,
          message: notificationMessage,
          type: 'task_assigned',
          actionLink: '/my-tasks',
          relatedDocId: docRef.id,
          createdBy: currentUser.uid,
          metadata: {
            taskId: docRef.id,
            priority,
            dueDate: new Date(dueDate)
          }
        }));
        
        notificationPromises.push(notificationPromise);
      }
      
      // Wait for all notification creation to complete
      await Promise.all(notificationPromises);
      
      // Send emails using the new EmailJS service if email sending is enabled
      if (sendEmails) {
        // Prepare task data for email
        const emailTaskData = {
          ...taskData,
          id: docRef.id,
          endDate: dueDate  // Use string date for email template
        };
        
        // Get full employee data for each assignee
        const assigneesData = assigneeEmails.map(email => getEmployeeDataByEmail(email));
        
        // Send emails to all assignees
        await sendTaskAssignmentEmails(emailTaskData, assignerData, assigneesData);
      }
      
      setSuccessMessage(`Task "${title}" created successfully! Notifications sent to assignees.`);
      
      if (onTaskCreated) {
        onTaskCreated({ id: docRef.id, ...taskData });
      }
      
      // Reset form after short delay to show success message
      setTimeout(() => {
        resetForm();
        onClose();
      }, 1500);
      
    } catch (error) {
      console.error('Error creating task:', error);
      setError(`Failed to create task: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };
  
  // Filtered employees based on search and department
  const filteredEmployees = employees && Array.isArray(employees)
    ? employees.filter(employee => {
        const matchesSearch = searchTerm === '' || 
          (employee.name && employee.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (employee.email && employee.email.toLowerCase().includes(searchTerm.toLowerCase()));
        
        const matchesDepartment = department === 'All' || employee.department === department;
        
        return matchesSearch && matchesDepartment;
      })
    : [];
  
  if (!show) return null;
  
  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content assign-task-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Assign New Task</h2>
          <button className="close-button" onClick={handleClose}>
            <HiX />
          </button>
        </div>
        
        {error && (
          <div className="error-message">
            <HiOutlineExclamation className="error-icon" />
            {error}
          </div>
        )}
        
        {successMessage && (
          <div className="success-message">
            {successMessage}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="task-title">Task Title*</label>
            <input
              id="task-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter task title"
              disabled={submitting}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="task-description">Description</label>
            <textarea
              id="task-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter task description with all relevant details"
              rows={4}
              disabled={submitting}
            ></textarea>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="due-date">Due Date & Time*</label>
              <div className="input-with-icon">
                <HiOutlineCalendar className="input-icon" />
                <input
                  id="due-date"
                  type="datetime-local"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  required
                  disabled={submitting}
                />
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="task-priority">Priority</label>
              <div className="input-with-icon">
                <HiOutlineTag className="input-icon" />
                <select
                  id="task-priority"
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  disabled={submitting}
                >
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                  <option value="Urgent">Urgent</option>
                </select>
              </div>
            </div>
          </div>
          
          <div className="form-group assignment-type">
            <label>Assignment Type</label>
            <div className="radio-group">
              <label className="radio-label">
                <input
                  type="radio"
                  checked={!roleBasedAssignment}
                  onChange={() => setRoleBasedAssignment(false)}
                  disabled={submitting}
                />
                Individual Assignment
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  checked={roleBasedAssignment}
                  onChange={() => setRoleBasedAssignment(true)}
                  disabled={submitting}
                />
                Role-Based Assignment
              </label>
            </div>
          </div>
          
          {!roleBasedAssignment ? (
            <div className="form-group">
              <label>Assign To*</label>
              
              <div className="form-row">
                <div className="form-group">
                  <input
                    type="text"
                    placeholder="Search by name or email"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    disabled={submitting}
                  />
                </div>
                
                <div className="form-group">
                  <div className="input-with-icon">
                    <HiOutlineOfficeBuilding className="input-icon" />
                    <select
                      value={department}
                      onChange={(e) => setDepartment(e.target.value)}
                      disabled={submitting}
                    >
                      {departments.map((dept, index) => (
                        <option key={index} value={dept}>{dept}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              
              <div className="assignees-list">
                {filteredEmployees.length > 0 ? (
                  filteredEmployees.map((employee) => (
                    <div 
                      key={employee.id} 
                      className="assignee-item"
                    >
                      <input
                        type="checkbox"
                        id={`assignee-${employee.id}`}
                        checked={selectedAssignees.includes(employee.id)}
                        onChange={() => {
                          const isSelected = selectedAssignees.includes(employee.id);
                          setSelectedAssignees(
                            isSelected
                              ? selectedAssignees.filter(id => id !== employee.id)
                              : [...selectedAssignees, employee.id]
                          );
                        }}
                        disabled={submitting}
                      />
                      <label htmlFor={`assignee-${employee.id}`} className="assignee-label">
                        <div className="assignee-icon">
                          {employee.img ? (
                            <img src={employee.img} alt={employee.name || 'Employee'} />
                          ) : (
                            <HiOutlineUserCircle />
                          )}
                        </div>
                        <div className="assignee-info">
                          <span className="assignee-name">{employee.name || 'Unnamed'}</span>
                          <span className="assignee-email">{employee.email}</span>
                        </div>
                      </label>
                    </div>
                  ))
                ) : (
                  <div className="no-results">No employees match your search criteria</div>
                )}
              </div>
            </div>
          ) : (
            <div className="form-group">
              <label>Role-Based Assignments*</label>
              {roleAssignments.map((assignment, index) => (
                <div key={index} className="role-assignment-row">
                  <div className="form-group user-select-group">
                    <select
                      value={assignment.email}
                      onChange={(e) => updateRoleAssignment(index, 'email', e.target.value)}
                      disabled={submitting}
                    >
                      <option value="">Select Employee</option>
                      {employees.map(employee => {
                        // Find the selected employee to display image in label
                        const isSelected = assignment.email === employee.email;
                        
                        return (
                          <option key={employee.id} value={employee.email}>
                            {employee.name || employee.email}
                          </option>
                        );
                      })}
                    </select>
                    {assignment.email && (
                      <div className="selected-user-preview">
                        {(() => {
                          const employee = employees.find(emp => emp.email === assignment.email);
                          if (!employee) return null;
                          
                          return (
                            <>
                              {employee.img ? (
                                <img src={employee.img} alt={employee.name || 'Employee'} />
                              ) : (
                                <HiOutlineUserCircle />
                              )}
                            </>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                  <div className="form-group">
                    <select
                      value={assignment.role}
                      onChange={(e) => updateRoleAssignment(index, 'role', e.target.value)}
                      disabled={submitting}
                    >
                      <option value="">Select Role</option>
                      <option value="Task Coordinator">Task Coordinator</option>
                      <option value="Reviewer">Reviewer</option>
                      <option value="Developer">Developer</option>
                      <option value="Designer">Designer</option>
                      <option value="Tester">Tester</option>
                      <option value="Project Manager">Project Manager</option>
                      <option value="Quality Assurance">Quality Assurance</option>
                    </select>
                  </div>
                  <button
                    type="button"
                    className="remove-role-button"
                    onClick={() => removeRoleAssignment(index)}
                    disabled={roleAssignments.length <= 1 || submitting}
                  >
                    <HiX />
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="add-role-button"
                onClick={addRoleAssignment}
                disabled={submitting}
              >
                <HiOutlinePlus />
                Add Another Role
              </button>
            </div>
          )}
          
          <div className="form-group notification-options">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={sendEmails}
                onChange={() => setSendEmails(!sendEmails)}
                disabled={submitting}
              />
              Send email notifications to assignees
            </label>
          </div>
          
          <div className="button-group">
            <button
              type="button"
              className="cancel-button"
              onClick={handleClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="submit-button"
              disabled={submitting}
            >
              {submitting ? 'Creating...' : 'Create Task'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AssignTaskModal;